"use client"

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { Search, X } from 'lucide-react'
import { debounce } from 'lodash'

interface SearchResult {
  id: string
  name: string
  title: string
  imageUrl?: string | null
  location?: string
}

interface SearchAutocompleteProps {
  placeholder?: string
  className?: string
  onSearch?: (query: string) => void
}

export default function SearchAutocomplete({ 
  placeholder = "Search by name, position, or keyword",
  className = "",
  onSearch
}: SearchAutocompleteProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showResults, setShowResults] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [chips, setChips] = useState<{ type: 'role'|'sector'; value: string }[]>([])
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search function
  const searchCandidates = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([])
        setShowResults(false)
        return
      }

      setIsLoading(true)
      try {
        const response = await fetch(`/api/search/autocomplete?q=${encodeURIComponent(searchQuery)}`)
        if (response.ok) {
          const data = await response.json()
          setResults(data.results || [])
          setShowResults(true)
        }
        // Also fetch suggestion chips (roles/sectors)
        const sugRes = await fetch(`/api/search/suggestions?q=${encodeURIComponent(searchQuery)}`)
        if (sugRes.ok) {
          const sug = await sugRes.json()
          let chipList: any = []
          if (Array.isArray(sug)) chipList = sug
          else if (Array.isArray(sug.data)) chipList = sug.data
          else if (Array.isArray(sug.results)) chipList = sug.results
          else if (sug?.data?.data && Array.isArray(sug.data.data)) chipList = sug.data.data
          const filtered = (Array.isArray(chipList) ? chipList : [])
            .filter((s: any) => s && (s.type === 'role' || s.type === 'sector') && typeof s.value === 'string')
            .slice(0, 6)
            .map((s: any) => ({ type: s.type, value: s.value }))
          setChips(filtered)
        } else {
          setChips([])
        }
      } catch (error) {
        console.error('Search error:', error)
        setResults([])
        setChips([])
      } finally {
        setIsLoading(false)
      }
    }, 300),
    []
  )

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    setSelectedIndex(-1)
    
    if (value.trim()) {
      searchCandidates(value)
    } else {
      setResults([])
      setShowResults(false)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || results.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelectResult(results[selectedIndex])
        } else if (query.trim()) {
          handleSearch()
        }
        break
      case 'Escape':
        setShowResults(false)
        setSelectedIndex(-1)
        break
    }
  }

  // Handle selecting a result
  const handleSelectResult = (result: SearchResult) => {
    router.push(`/profiles/${result.id}`)
    setShowResults(false)
    setQuery('')
  }

  // Handle search submit
  const handleSearch = () => {
    if (query.trim()) {
      if (onSearch) {
        onSearch(query)
      } else {
        router.push(`/?view=profiles&q=${encodeURIComponent(query)}`)
      }
      setShowResults(false)
    }
  }

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    handleSearch()
  }

  // Clear search
  const handleClear = () => {
    setQuery('')
    setResults([])
    setShowResults(false)
    inputRef.current?.focus()
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && results.length > 0 && setShowResults(true)}
            placeholder={placeholder}
            className="w-full px-6 py-4 pr-24 bg-white border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#7294c7] focus:border-transparent shadow-sm transition-all duration-300 text-lg"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="search-results"
            aria-expanded={showResults}
          />
          
          <div className="absolute right-3 flex items-center gap-2">
            {query && (
              <button
                type="button"
                onClick={handleClear}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
                aria-label="Clear search"
              >
                <X className="w-5 h-5" />
              </button>
            )}
            
            <button
              type="submit"
              className="p-3 bg-[#7294c7] text-white rounded-full hover:bg-[#5d7cb8] transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>
        </div>
      </form>

      {/* Suggestion chips (roles/sectors) */}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2 px-2">
          {chips.map((c, i) => (
            <button
              key={`${c.type}-${c.value}-${i}`}
              type="button"
              className="px-4 py-1.5 text-xs rounded-full border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 transition-colors"
              onClick={() => {
                setQuery(c.value)
                setShowResults(false)
                onSearch?.(c.value)
              }}
            >
              {c.value}
            </button>
          ))}
        </div>
      )}

      {/* Search Results Dropdown */}
      {showResults && (
        <div 
          id="search-results"
          className="absolute z-50 w-full mt-3 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-96 overflow-y-auto ring-1 ring-black/5"
          role="listbox"
        >
          {isLoading ? (
            <div className="px-4 py-3 text-center text-gray-500">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <ul className="py-2">
              {results.map((result, index) => (
                <li key={result.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectResult(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                      selectedIndex === index ? 'bg-gray-50' : ''
                    }`}
                    role="option"
                    aria-selected={selectedIndex === index}
                  >
                    {/* Profile Image */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gray-200 rounded-full overflow-hidden">
                      {result.imageUrl ? (
                        <Image
                          src={result.imageUrl}
                          alt=""
                          width={48}
                          height={48}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm font-semibold">
                          {result.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                      )}
                    </div>
                    
                    {/* Profile Info */}
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-gray-900">{result.name}</div>
                      <div className="text-sm text-gray-600">{result.title}</div>
                      {result.location && (
                        <div className="text-xs text-gray-500">{result.location}</div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : query.length >= 2 ? (
            <div className="px-4 py-3 text-center text-gray-500">
              No results found for "{query}"
            </div>
          ) : null}
          
          {results.length > 0 && (
            <div className="border-t border-gray-200 px-4 py-2">
              <button
                type="button"
                onClick={handleSearch}
                className="text-sm text-[#7294c7] hover:text-[#5d7cb8] font-medium"
              >
                View all results for "{query}" →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
